import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet,
  ShoppingBag,
  Clock,
  Utensils,
  Receipt as ReceiptIcon,
  AlertTriangle,
  TrendingUp,
  Flame,
  Plus,
  ChefHat,
  Banknote,
  Package,
  CalendarDays,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import { api, apiError } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/contexts/I18nContext";
import { StatCard, Section, EmptyState, StatusBadge, LoadError } from "@/components/Primitives";
import { StaggerRoot, StaggerItem, MotionButton } from "@/components/motion";
import MotionCard from "@/components/motion/MotionCard";

export default function Dashboard() {
  const { t, money, lang } = useI18n();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => api.get("/dashboard/stats").then((r) => r.data),
    retry: 1,
  });

  if (isLoading)
    return <div className="p-10 text-gray-500">{t("loading")}</div>;
  if (isError)
    return <LoadError message={apiError(error)} onRetry={() => refetch()} />;

  const safe = {
    trend: data?.trend || [],
    top_items: data?.top_items || [],
    recent_orders: data?.recent_orders || [],
    recent_debtors: data?.recent_debtors || [],
    low_stock_items: data?.low_stock_items || [],
    today_reservations: data?.today_reservations || [],
    ...data,
  };

  return (
    <StaggerRoot className="space-y-6">
      {/* Header row */}
      <StaggerItem index={0}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">
            {t("dashboard")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t("welcome")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <MotionButton
            data-testid="quick-new-order"
            onClick={() => navigate("/new-order")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] hover:bg-[#7F1020] text-white text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> {t("newOrder")}
          </MotionButton>
          <MotionButton
            onClick={() => navigate("/cashier")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <Wallet className="w-4 h-4" /> {t("goToCashier")}
          </MotionButton>
          <MotionButton
            onClick={() => navigate("/kitchen")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <ChefHat className="w-4 h-4" /> {t("viewKitchen")}
          </MotionButton>
          <MotionButton
            onClick={() => navigate("/expenses")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <ReceiptIcon className="w-4 h-4" /> {t("addExpense")}
          </MotionButton>
        </div>
      </div>
      </StaggerItem>

      {/* Stats grid */}
      <StaggerRoot className="grid grid-cols-2 md:grid-cols-4 gap-4" delay={0.05}>
        <StaggerItem index={0}><StatCard testId="stat-sales" icon={Banknote} label={t("salesToday")} value={money(safe.sales_today)} accent="#071A33" /></StaggerItem>
        <StaggerItem index={1}><StatCard testId="stat-orders" icon={ShoppingBag} label={t("ordersToday")} value={safe.orders_today} accent="#2563EB" /></StaggerItem>
        <StaggerItem index={2}><StatCard testId="stat-active" icon={Clock} label={t("activeOrders")} value={safe.active_orders} accent="#F59E0B" /></StaggerItem>
        <StaggerItem index={3}><StatCard testId="stat-busy" icon={Utensils} label={t("busyTables")} value={`${safe.busy_tables} / ${safe.total_tables}`} accent="#DC2626" /></StaggerItem>
        <StaggerItem index={4}><StatCard testId="stat-expense" icon={ReceiptIcon} label={t("expenseToday")} value={money(safe.expense_today)} accent="#6B7280" /></StaggerItem>
        <StaggerItem index={5}><StatCard testId="stat-debt" icon={AlertTriangle} label={t("debtToday")} value={money(safe.debt_today)} accent="#F97316" /></StaggerItem>
        <StaggerItem index={6}><StatCard testId="stat-profit" icon={TrendingUp} label={t("profitToday")} value={money(safe.profit_today)} accent="#16A34A" /></StaggerItem>
        <StaggerItem index={7}><StatCard testId="stat-items" icon={Flame} label={t("itemsSold")} value={safe.items_sold} accent="#071A33" /></StaggerItem>
      </StaggerRoot>

      {(safe.low_stock_count > 0 || safe.reservations_today > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {safe.low_stock_count > 0 && (
            <MotionCard data-testid="dash-low-stock" className="rp-card p-5 border-rose-200 bg-rose-50/40 flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-rose-100 text-rose-700 grid place-items-center shrink-0">
                <Package className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="font-bold text-rose-800">{t("lowStock")}</div>
                    <div className="text-xs text-rose-700/80">{safe.low_stock_count} {lang === "pashto" ? "خام مواد" : "ماده اولیه"}</div>
                  </div>
                  <button onClick={() => navigate("/inventory")} className="text-xs font-medium text-rose-700 hover:underline">{t("inventory")} →</button>
                </div>
                <div className="space-y-1">
                  {safe.low_stock_items.map((it) => (
                    <div key={it.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-800">{lang === "pashto" ? it.name_pashto : it.name_dari}</span>
                      <span className="tabular text-rose-700 font-medium">{it.quantity} / {it.min_quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </MotionCard>
          )}
          {safe.reservations_today > 0 && (
            <MotionCard data-testid="dash-reservations" className="rp-card p-5 border-sky-200 bg-sky-50/40 flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 grid place-items-center shrink-0">
                <CalendarDays className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="font-bold text-sky-800">{t("reservationsToday")}</div>
                    <div className="text-xs text-sky-700/80">{safe.reservations_today} {lang === "pashto" ? "ریزرویشن" : "رزرو"}</div>
                  </div>
                  <button onClick={() => navigate("/reservations")} className="text-xs font-medium text-sky-700 hover:underline">{t("reservations")} →</button>
                </div>
                <div className="space-y-1">
                  {safe.today_reservations.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-800">{r.customer_name}</span>
                      <span className="tabular font-mono text-sky-700">{r.time} • {r.party_size}</span>
                    </div>
                  ))}
                </div>
              </div>
            </MotionCard>
          )}
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Section title={t("salesTrend")}>
            <div style={{ width: "100%", minWidth: 0 }}>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={safe.trend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#071A33" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#071A33" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#9ca3af" fontSize={11} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12, direction: "rtl" }}
                    formatter={(v) => money(v)}
                  />
                  <Area dataKey="sales" stroke="#071A33" strokeWidth={2.5} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>

        <Section title={t("topItems")}>
          {!safe.top_items?.length ? (
            <EmptyState icon={Flame} title={t("empty")} />
          ) : (
            <div className="space-y-3">
              {safe.top_items.map((it, i) => (
                <div key={it.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg grid place-items-center text-xs font-bold shrink-0"
                      style={{
                        backgroundColor: i === 0 ? "#071A33" : i === 1 ? "#071A3322" : "#f3f4f6",
                        color: i === 0 ? "#fff" : i === 1 ? "#071A33" : "#6b7280",
                      }}
                    >
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {lang === "pashto" ? it.name_pashto : it.name_dari}
                      </div>
                      <div className="text-xs text-gray-500 tabular">
                        {it.qty} × — {money(it.revenue)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Section title={t("recentOrders")}>
          {!safe.recent_orders?.length ? (
            <EmptyState icon={ShoppingBag} title={t("empty")} />
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <table className="w-full text-right text-sm">
                <thead className="text-xs text-gray-500">
                  <tr><th className="py-2">#</th><th>{t("total")}</th><th>{t("status")}</th></tr>
                </thead>
                <tbody>
                  {safe.recent_orders.slice(0, 6).map((o) => (
                    <tr key={o.id} className="border-t border-gray-100">
                      <td className="py-2.5 font-mono text-gray-600">{o.order_number?.slice(-4)}</td>
                      <td className="py-2.5 tabular text-gray-900 font-medium">{money(o.paid_amount || o.total)}</td>
                      <td className="py-2.5"><StatusBadge status={o.payment_status} label={t(o.payment_status)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <div className="lg:col-span-2">
          <Section title={t("recentDebtors")}>
            {!safe.recent_debtors?.length ? (
              <EmptyState icon={AlertTriangle} title={t("empty")} />
            ) : (
              <div className="overflow-x-auto thin-scroll">
                <table className="w-full text-right text-sm">
                  <thead className="text-xs text-gray-500">
                    <tr>
                      <th className="py-2">{t("name")}</th>
                      <th>{t("phone")}</th>
                      <th>{t("totalDebt")}</th>
                      <th>{t("remainingDebt")}</th>
                      <th>{t("status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safe.recent_debtors.map((d) => (
                      <tr key={d.id} className="border-t border-gray-100">
                        <td className="py-2.5 text-gray-900">{d.name}</td>
                        <td className="py-2.5 text-gray-500 tabular">{d.phone || "-"}</td>
                        <td className="py-2.5 tabular">{money(d.total_debt)}</td>
                        <td className="py-2.5 tabular text-rose-600 font-medium">{money(d.remaining_debt)}</td>
                        <td className="py-2.5"><StatusBadge status={d.status} label={t(d.status)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      </div>
    </StaggerRoot>
  );
}
